import { memo, useState } from 'react';
import { cn } from '@/utils/cn';
import { Typography } from '@/components/ui/_r/typography';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import Label from '@/components/ui/_r/inputs/label';
import useFilterTicketModal from './use-filter-ticket-modal';
import MultiselectAutoComplete from '@/components/ui/_r/inputs/multiselect-autocomplete';
// import { MemberOption, SelectedMemberItem } from '../_shared/ticket-member-option';
// import { LabelOption, SelectedLabelItem } from '../_shared/label-ticket-option';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/_shadcn-ui/tooltip';
import { RotateCcw } from 'lucide-react';
import TimeFrameModal from '@/components/ui/timeframe-modal';
import Input from '@/components/ui/_r/inputs/input';
import { formatDateRange } from '@/features/ticketings/tickets/ticket/utils/ticket.utils';
import { handleDomBubbleEvent } from '@/utils/dom';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import {
  MemberOption,
  SelectedMemberItem,
} from '@/features/ticketing/tickets/components/modals/_shared/ticket-member-option';
import {
  LabelOption,
  SelectedLabelItem,
} from '@/features/ticketing/tickets/components/modals/_shared/label-ticket-option';

export type TFilterTicketModalType = 'FilterTicketModal';
export interface IFilterTicketModalProps {
  spaceId: string;
  spaceName: string;
}
const FilterTicketModal = memo(({ spaceId, spaceName }: IFilterTicketModalProps) => {
  // Local state for timeframe modals
  const [isTimeFrameModalOpen, setIsTimeFrameModalOpen] = useState(false);
  const [isCreatedAtTimeFrameModalOpen, setIsCreatedAtTimeFrameModalOpen] = useState(false);

  // HOOKS
  const {
    hasChanges,
    isOpen,
    handleCloseModal,
    handleCancelModal,
    handleFilter,
    handleClearAll,
    isHasFilter,
    // State
    pendingTitleSearch,
    pendingIsCompleted,
    pendingIsNotCompleted,
    pendingIsNoMember,
    pendingIsNoLabelTicket,
    pendingNoDate,
    pendingOverDue,
    pendingDueInNextDay,
    pendingDueInNextDays,
    pendingDueInNextMonth,
    // Member data
    listTicketMemberOptions,
    selectedMemberOptions,
    isLoadingListTicketMemberData,
    // Creator data
    selectedCreatorOptions,
    // Label data
    listLabelTicketOptions,
    selectedLabelOptions,
    isLoadingListLabelTicketData,
    // Date helpers
    today,
    nextDay,
    nextWeek,
    nextMonth,
    formatDate,
    // Handlers
    handleTitleSearchChange,
    handleIsCompletedChange,
    handleIsNotCompletedChange,
    handleNoMemberChange,
    handleMemberCheckboxChange,
    handleMemberChange,
    handleMemberClearAll,
    handleMemberRemove,
    handleCreatorCheckboxChange,
    handleCreatorChange,
    handleCreatorClearAll,
    handleCreatorRemove,
    handleNoDateChange,
    handleOverDueChange,
    handleDueInNextDayChange,
    handleDueInNextDaysChange,
    handleDueInNextMonthChange,
    handleNoLabelTicketChange,
    handleLabelCheckboxChange,
    handleLabelChange,
    handleLabelClearAll,
    handleLabelRemove,
    // Timeframe
    pendingTimeFrameDateRange,
    handleTimeFrameChange,
    handleTimeFrameClear,
    // CreatedAt timeframe
    pendingCreatedAtDateRange,
    handleCreatedAtTimeFrameChange,
    handleCreatedAtTimeFrameClear,
  } = useFilterTicketModal({ spaceId });

  // RENDER
  return (
    <Modal
      isOpen={isOpen}
      isCloseOutside={false}
      isButtonClose={true}
      onClose={handleCloseModal}
      className="md:w-[500px] w-[345px] p-[32px]"
    >
      <ModalHeader>
        <ModalTitle>Filter Tickets in {spaceName} Space</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-[4px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Title</Typography>
          <div>
            <Input
              placeholder="Search Title Ticket"
              value={pendingTitleSearch}
              type="text"
              onChange={(e) => handleTitleSearchChange(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
        <div className="space-y-[4px] mt-[8px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Creator</Typography>
          <div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                disabled={isLoadingListTicketMemberData}
                checked={selectedCreatorOptions.length > 0}
                onCheckedChange={handleCreatorCheckboxChange}
              />
              <MultiselectAutoComplete
                labelKey="fullname"
                valueKey="userId"
                placeholder={isLoadingListTicketMemberData ? 'Loading Creators...' : 'Select creators'}
                searchPlaceholder="Search creators"
                disabled={isLoadingListTicketMemberData}
                isLoading={isLoadingListTicketMemberData}
                options={listTicketMemberOptions}
                value={selectedCreatorOptions}
                onChange={handleCreatorChange}
                onClearAll={handleCreatorClearAll}
                renderOption={(option) => <MemberOption option={option} />}
                renderSelectedItem={(option) => <SelectedMemberItem option={option} onRemove={handleCreatorRemove} />}
              />
            </div>
          </div>
        </div>

        <div className="space-y-[4px] mt-[8px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Member</Typography>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox checked={pendingIsNoMember} onCheckedChange={handleNoMemberChange} />
              <Typography className="text-[#25151E] text-[12px] font-[400]">No Members</Typography>
            </Label>
          </div>
          <div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                disabled={isLoadingListTicketMemberData || pendingIsNoMember}
                checked={selectedMemberOptions.length > 0}
                onCheckedChange={handleMemberCheckboxChange}
              />
              <MultiselectAutoComplete
                labelKey="fullname"
                valueKey="userId"
                placeholder={isLoadingListTicketMemberData ? 'Loading Members...' : 'Select members'}
                searchPlaceholder="Search members"
                disabled={isLoadingListTicketMemberData || pendingIsNoMember}
                isLoading={isLoadingListTicketMemberData}
                options={listTicketMemberOptions}
                value={selectedMemberOptions}
                onChange={handleMemberChange}
                onClearAll={handleMemberClearAll}
                renderOption={(option) => <MemberOption option={option} />}
                renderSelectedItem={(option) => <SelectedMemberItem option={option} onRemove={handleMemberRemove} />}
              />
            </div>
          </div>
        </div>

        <div className="space-y-[10px] mt-[8px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Ticket Status</Typography>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox checked={pendingIsCompleted} onCheckedChange={handleIsCompletedChange} />
              <Typography className="text-[#25151E] text-[12px] font-[400]">Completed</Typography>
            </Label>
          </div>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox checked={pendingIsNotCompleted} onCheckedChange={handleIsNotCompletedChange} />
              <Typography className="text-[#25151E] text-[12px] font-[400]">Not Completed</Typography>
            </Label>
          </div>
        </div>

        <div className="space-y-[4px] mt-[8px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Created Date</Typography>
          <div>
            <div className="flex items-center  gap-[8px]  justify-between">
              <Checkbox
                checked={pendingCreatedAtDateRange !== undefined}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setIsCreatedAtTimeFrameModalOpen(true);
                  } else {
                    handleCreatedAtTimeFrameClear();
                  }
                }}
              />
              <div className=" w-full">
                <Input
                  placeholder="Set Created date"
                  value={formatDateRange(pendingCreatedAtDateRange)}
                  type="text"
                  readOnly
                  className="cursor-pointer"
                  onClick={() => setIsCreatedAtTimeFrameModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-[10px] mt-[10px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Due Date</Typography>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox
                checked={pendingNoDate}
                onCheckedChange={handleNoDateChange}
                disabled={pendingTimeFrameDateRange !== undefined}
              />
              <Typography className="text-[#25151E] text-[12px] font-[400]">No Date</Typography>
            </Label>
          </div>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox
                checked={pendingOverDue}
                onCheckedChange={handleOverDueChange}
                disabled={pendingTimeFrameDateRange !== undefined}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[#25151E] text-[12px] font-[400]  inline-block">Overdue</span>
                </TooltipTrigger>
                <TooltipContent className="z-[1000] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border border-[#D9D9D9]">
                  <p>Before {formatDate(today)}</p>
                </TooltipContent>
              </Tooltip>
            </Label>
          </div>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox
                checked={pendingDueInNextDay}
                onCheckedChange={handleDueInNextDayChange}
                disabled={pendingTimeFrameDateRange !== undefined}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[#25151E] text-[12px] font-[400] cursor-pointer inline-block">
                    Due in the next day
                  </span>
                </TooltipTrigger>
                <TooltipContent className="z-[1000] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border border-[#D9D9D9]">
                  <p>{formatDate(nextDay)}</p>
                </TooltipContent>
              </Tooltip>
            </Label>
          </div>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox
                checked={pendingDueInNextDays}
                onCheckedChange={handleDueInNextDaysChange}
                disabled={pendingTimeFrameDateRange !== undefined}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[#25151E] text-[12px] font-[400] cursor-pointer inline-block">
                    Due in the next week
                  </span>
                </TooltipTrigger>
                <TooltipContent className="z-[1000] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border border-[#D9D9D9]">
                  <p>Until {formatDate(nextWeek)}</p>
                </TooltipContent>
              </Tooltip>
            </Label>
          </div>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox
                checked={pendingDueInNextMonth}
                onCheckedChange={handleDueInNextMonthChange}
                disabled={pendingTimeFrameDateRange !== undefined}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[#25151E] text-[12px] font-[400] cursor-pointer inline-block">
                    Due in the next month
                  </span>
                </TooltipTrigger>
                <TooltipContent className="z-[1000] bg-white text-[#25151E] text-[12px] font-normal p-2 rounded-lg border border-[#D9D9D9]">
                  <p>Until {formatDate(nextMonth)}</p>
                </TooltipContent>
              </Tooltip>
            </Label>
          </div>

          <div className="flex items-center  gap-[8px]  justify-between">
            <Checkbox
              checked={pendingTimeFrameDateRange !== undefined}
              onCheckedChange={(checked) => {
                if (checked) {
                  setIsTimeFrameModalOpen(true);
                } else {
                  handleTimeFrameClear();
                }
              }}
            />
            <div className=" w-full">
              <Input
                placeholder="Set Due date"
                value={formatDateRange(pendingTimeFrameDateRange)}
                type="text"
                readOnly
                className="cursor-pointer"
                onClick={() => setIsTimeFrameModalOpen(true)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-[4px] mt-[8px]">
          <Typography className="text-[#9D9D9C] text-[12px] font-[400]">Labels</Typography>
          <div>
            <Label className="flex items-center gap-[8px] w-fit cursor-pointer">
              <Checkbox checked={pendingIsNoLabelTicket} onCheckedChange={handleNoLabelTicketChange} />
              <Typography className="text-[#25151E] text-[12px] font-[400]">No Labels</Typography>
            </Label>
          </div>
          <div>
            <div className="flex items-center gap-[8px]">
              <Checkbox
                disabled={isLoadingListLabelTicketData || pendingIsNoLabelTicket}
                checked={selectedLabelOptions.length > 0}
                onCheckedChange={handleLabelCheckboxChange}
              />
              <MultiselectAutoComplete
                labelKey="name"
                valueKey="labelTicketId"
                placeholder={isLoadingListLabelTicketData ? 'Loading Labels...' : 'Select labels'}
                searchPlaceholder="Search labels"
                disabled={isLoadingListLabelTicketData || pendingIsNoLabelTicket}
                isLoading={isLoadingListLabelTicketData}
                options={listLabelTicketOptions}
                value={selectedLabelOptions}
                onChange={handleLabelChange}
                onClearAll={handleLabelClearAll}
                renderOption={(option) => <LabelOption option={option} />}
                renderSelectedItem={(option) => <SelectedLabelItem option={option} onRemove={handleLabelRemove} />}
              />
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <button
          type="button"
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClearAll(e);
          }}
          disabled={!isHasFilter}
          className={cn(
            'disabled:cursor-not-allowed disabled:bg-[#9D9D9C] disabled:border-[#9D9D9C] disabled:text-[#FFF] text-[14px] font-normal px-[16px] rounded-[8px] py-[8px] border border-white bg-white text-[#25151E] cursor-pointer hover:bg-[#25151E] hover:border-[#25151E] hover:text-white w-full sm:w-auto flex items-center justify-center gap-[4px]'
          )}
        >
          <RotateCcw className="w-4 h-4" />
          Clear All
        </button>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto items-stretch sm:items-center gap-[12px]">
          <ModalCloseButton
            onClick={(e) => {
              handleDomBubbleEvent(e);
              handleCancelModal(e);
            }}
            className="w-full sm:w-auto"
          >
            Cancel
          </ModalCloseButton>
          <ModalConfirmButton
            onClick={(e) => {
              handleDomBubbleEvent(e);
              handleFilter(e);
            }}
            disabled={!hasChanges}
            className="w-full sm:w-auto"
          >
            Save Changes
          </ModalConfirmButton>
        </div>
      </ModalFooter>
      <TimeFrameModal
        isOpen={isTimeFrameModalOpen}
        onClose={() => setIsTimeFrameModalOpen(false)}
        onClear={() => {
          handleTimeFrameClear();
        }}
        onApply={(dateRange) => {
          handleTimeFrameChange(dateRange);
          setIsTimeFrameModalOpen(false);
        }}
        initialDateRange={pendingTimeFrameDateRange}
        title="Filter by Due Date Time Frame"
        submitText="Apply Filters"
      />
      <TimeFrameModal
        isOpen={isCreatedAtTimeFrameModalOpen}
        onClose={() => setIsCreatedAtTimeFrameModalOpen(false)}
        onClear={() => {
          handleCreatedAtTimeFrameClear();
        }}
        onApply={(dateRange) => {
          handleCreatedAtTimeFrameChange(dateRange);
          setIsCreatedAtTimeFrameModalOpen(false);
        }}
        initialDateRange={pendingCreatedAtDateRange}
        title="Filter by Created Date Time Frame"
        submitText="Apply Filters"
      />
    </Modal>
  );
});
FilterTicketModal.displayName = 'FilterTicketModal';
export default FilterTicketModal;
